import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Close } from "@mui/icons-material";
import { Button, DialogActions } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";

import { isChunkLoadError } from "@features/appVersion/deployVersionGuard";
import { markAppStale } from "@features/appVersion/staleAppState";
import { clearError } from "@redux/slices/errorSlice";

const ErrorModal = () => {
  const dispatch = useDispatch();

  const error = useSelector((state) => state.error.currentError);
  const source = useSelector((state) => state.error.errorSource);

  const handleClose = () => dispatch(clearError());

  // Chunk-load failures after a deploy are not actionable errors: the chunk
  // is gone until the page reloads. Instead of showing the raw message, defer
  // to the globally mounted StaleReloadModal (see its docstring for why it is
  // a separate dialog). Marking the app stale here is a safety net for paths
  // where the deployVersionGuard listeners didn't fire (e.g. the plain dev
  // server, where Vite doesn't wrap dynamic imports with its preload helper);
  // without it, suppressing the error would fail silently.
  const isStaleChunkError = !!error && isChunkLoadError(error);
  useEffect(() => {
    if (isStaleChunkError) markAppStale("chunk-load-error");
  }, [isStaleChunkError]);

  if (!error || isStaleChunkError) return null;

  const errorMessages = String(error)
    .split("\n")
    .map((message) => message.trim())
    .filter(Boolean);
  const hasMultipleErrors = errorMessages.length > 1;

  return (
    <Dialog
      open
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={{ zIndex: 1305 }}
    >
      <DialogContent>
        <div className="flex items-start justify-between mb-2">
          <div>
            <h2 className="text-2xl font-medium">Something went wrong</h2>
            <div className="text-base text-neutral-500">
              {hasMultipleErrors
                ? "The following errors have occurred:"
                : "The following error has occurred:"}
            </div>
          </div>
          <IconButton edge="end" onClick={handleClose}>
            <Close />
          </IconButton>
        </div>
        <div className="text-lg text-neutral-800 bg-red-50 p-4 rounded border-l-4 border-red-200">
          {hasMultipleErrors ? (
            <ul className="list-disc pl-5 space-y-2">
              {errorMessages.map((message, index) => (
                <li key={`${index}-${message}`}>{message}</li>
              ))}
            </ul>
          ) : (
            <p className="whitespace-pre-wrap">{errorMessages[0] ?? error}</p>
          )}
        </div>
        {source && (
          <p className="text-sm text-neutral-500 mt-2">{`Source: ${source}`}</p>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          GO BACK HOME
        </Button>

        <Button
          variant="contained"
          onClick={handleClose}
          style={{ marginLeft: "12px" }}
        >
          DISMISS ERROR
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ErrorModal;
